import { Title } from '@solidjs/meta'
import { useParams, useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createResource, onCleanup, Show } from 'solid-js'
import Card from '~/components/Card'
import GoBack from '~/components/GoBack'
import Skeleton from '~/components/Skeleton'
import { useToast } from '~/components/useToast'
import ProductForm from '~/layers/ProductForm'
import { productService } from '~/services/product.service'
import { setTrailOverride } from '~/stores/breadcrumb'
import { resolveError } from '~/utils/error'

/**
 * Ficha de un elemento del catálogo. Espera al recurso antes de pintar el
 * formulario: darlo por inicializado mientras la petición viaja lo dejaría
 * vacío, y guardar encima borraría los datos.
 */
const ProductoPage: Component = () => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const toast = useToast()

  // El título de la pestaña se congela al montar: solid-meta relee los hijos de
  // <Title> al desmontar, cuando el recurso ya está desechado.
  const documentTitle = 'Ficha del catálogo | Florez Jaramillo'

  /** Cambiar de pestaña se refleja en la URL (?tab=...) para poder compartir y recargar la vista. */
  const handleTabChange = (tab: string) => {
    setSearchParams({ ...searchParams, tab }, { replace: true })
  }

  const [product, { mutate }] = createResource(
    () => Number(params.id),
    async (id: number) => {
      try {
        return await productService.get(id)
      } catch (err: unknown) {
        toast.addToast({ title: resolveError(err), type: 'danger' })
        return null
      }
    }
  )

  // Miga de pan propia: "Catálogo › [nombre]". Se limpia al salir de la ficha.
  createEffect(() => {
    const name = product()?.name
    if (name) setTrailOverride(['Catálogo', name])
  })
  onCleanup(() => setTrailOverride(null))

  return (
    <>
      <Title>{documentTitle}</Title>

      <GoBack label="Volver al catálogo" fallbackPath="/admin/servicios" class="mb-3 text-foreground-subtle hover:text-foreground" />

      <Show
        when={!product.loading}
        fallback={
          <Card>
            <Card.Body>
              <div class="space-y-3">
                <Skeleton height="2rem" width="40%" />
                <Skeleton height="1rem" width="70%" />
                <Skeleton height="12rem" />
              </div>
            </Card.Body>
          </Card>
        }
      >
        <Show
          when={product()}
          fallback={
            <Card>
              <Card.Body>
                <p class="py-8 text-center text-foreground-subtle">No encontramos este elemento del catálogo.</p>
              </Card.Body>
            </Card>
          }
        >
          {data => (
            <ProductForm
              product={data()}
              initialTab={typeof searchParams.tab === 'string' ? searchParams.tab : undefined}
              onTabChange={handleTabChange}
              // Se refresca en el sitio: guardar no debe sacarte de la ficha.
              onSaved={saved => mutate(saved)}
            />
          )}
        </Show>
      </Show>
    </>
  )
}

export default ProductoPage
